import {convertToUserTimezone} from "./helpers.js";

// Facility State / Checksheet State only: user-defined operating-mode tags

export const DEFAULT_FACILITY_STATE_ID = "default";

export const EXTERNAL_OUTCOME = {
  FORM: "form",
  COMPLETED: "completed",
  STATE_MISMATCH: "state_mismatch",
  NOT_DUE: "not_due",
  SESSION_EXPIRED: "session_expired"
};

/** Facility state id the checksheet has a variant defined for (if any). */
export const getDefinedFacilityStateId = checksheet => {
  if (checksheet?.state?.facilityStateId == null) return null;
  return `${checksheet.state.facilityStateId}`;
};

/**
 * Whether the facility state this checksheet is tied to is currently on.
 * Facilities can have multiple states on at once — only this checksheet's
 * defined variant matters for external reminder links.
 */
export const isDefinedFacilityStateActive = checksheet =>
  Boolean(checksheet?.state && checksheet.stateActive && checksheet.state.facilityStateId != null);

/** "ON CRUSH" / "OFF CRUSH" without doubling if name already includes ON/OFF. */
export const formatFacilityStateOnOff = (onOrOff, stateName) => {
  const label = `${stateName || "UNKNOWN"}`.toUpperCase().trim();
  const stripped = label.replace(/^(ON|OFF)\s+/, "");
  return `${`${onOrOff}`.toUpperCase()} ${stripped}`;
};

export const getFacilityStateNameById = (
  facilityStateId,
  facilityStates = [],
  checksheet = null
) => {
  if (!facilityStateId || facilityStateId === DEFAULT_FACILITY_STATE_ID) {
    return "DEFAULT";
  }

  const match = facilityStates.find(({id}) => id === `${facilityStateId}`);
  if (match?.name) return match.name;

  const fromChecksheet =
    checksheet?.state?.facilityStateId != null &&
    `${checksheet.state.facilityStateId}` === `${facilityStateId}`
      ? checksheet.state?.state?.name
      : null;

  return fromChecksheet ? fromChecksheet.toUpperCase() : "UNKNOWN";
};

/**
 * Resolve name / builder / frequency for the checksheet variant the link points at,
 * not whichever facility state happens to be active right now.
 *
 * For a completed record, prefer the record's own state so the pill matches
 * what was actually submitted (ignore URL facilityStateId).
 */
export const getLinkedChecksheetDisplay = ({
  expectedFacilityStateId,
  checksheet,
  record = null,
  preferRecordState = false
}) => {
  if (!checksheet) return null;

  if (preferRecordState && record) {
    const recordState = record.state;
    if (recordState?.facilityStateId != null) {
      return {
        name: recordState.name ?? checksheet.name,
        builder: record.builder ?? recordState.builder ?? checksheet.builder,
        frequency: recordState.frequency ?? checksheet.frequency,
        lastDay: recordState.lastDay ?? checksheet.lastDay,
        availableIndex: recordState.availableIndex ?? checksheet.availableIndex,
        availableBase: recordState.availableBase ?? checksheet.availableBase,
        facilityStateId: `${recordState.facilityStateId}`,
        showStatePill: true
      };
    }

    const defaults = checksheet.default || {};
    return {
      name: defaults.name ?? checksheet.name,
      builder: record.builder ?? defaults.builder ?? checksheet.builder,
      frequency: defaults.frequency ?? checksheet.frequency,
      lastDay: defaults.lastDay ?? checksheet.lastDay,
      availableIndex: defaults.availableIndex ?? checksheet.availableIndex,
      availableBase: defaults.availableBase ?? checksheet.availableBase,
      facilityStateId: DEFAULT_FACILITY_STATE_ID,
      showStatePill: false
    };
  }

  const linkedDefault =
    !expectedFacilityStateId || expectedFacilityStateId === DEFAULT_FACILITY_STATE_ID;

  if (linkedDefault) {
    const defaults = checksheet.default || {};
    return {
      name: defaults.name ?? checksheet.name,
      builder: defaults.builder ?? checksheet.builder,
      frequency: defaults.frequency ?? checksheet.frequency,
      lastDay: defaults.lastDay ?? checksheet.lastDay,
      availableIndex: defaults.availableIndex ?? checksheet.availableIndex,
      availableBase: defaults.availableBase ?? checksheet.availableBase,
      facilityStateId: DEFAULT_FACILITY_STATE_ID,
      showStatePill: false
    };
  }

  const variant = checksheet.state;
  const variantMatches =
    variant?.facilityStateId != null &&
    `${variant.facilityStateId}` === `${expectedFacilityStateId}`;

  if (variantMatches) {
    return {
      name: variant.name ?? checksheet.name,
      builder: variant.builder ?? checksheet.builder,
      frequency: variant.frequency ?? checksheet.frequency,
      lastDay: variant.lastDay ?? checksheet.lastDay,
      availableIndex: variant.availableIndex ?? checksheet.availableIndex,
      availableBase: variant.availableBase ?? checksheet.availableBase,
      facilityStateId: `${variant.facilityStateId}`,
      showStatePill: true
    };
  }

  const defaults = checksheet.default || {};
  return {
    name: defaults.name ?? checksheet.name,
    builder: defaults.builder ?? checksheet.builder,
    frequency: defaults.frequency ?? checksheet.frequency,
    lastDay: defaults.lastDay ?? checksheet.lastDay,
    availableIndex: defaults.availableIndex ?? checksheet.availableIndex,
    availableBase: defaults.availableBase ?? checksheet.availableBase,
    facilityStateId: expectedFacilityStateId,
    showStatePill: true
  };
};

/**
 * Normalize to a local calendar YYYY-MM-DD.
 * Bare date strings from email links are already local; datetimes are UTC
 * end-of-local-day values and must be converted before taking the day.
 */
export const toCalendarDay = (value, timezone = "US/Pacific") => {
  if (!value) return null;
  const raw = `${value}`.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  try {
    return convertToUserTimezone(value, timezone).format("YYYY-MM-DD");
  } catch {
    const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
  }
};

export const getTodayCalendarDay = (timezone = "US/Pacific") => {
  try {
    return convertToUserTimezone(new Date().toISOString(), timezone).format("YYYY-MM-DD");
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
};

/** Linked day is strictly before today in the facility timezone. */
export const isLinkedDayPast = (linkedDateDue, timezone = "US/Pacific") => {
  const linkedDay = toCalendarDay(linkedDateDue, timezone);
  const today = getTodayCalendarDay(timezone);
  if (!linkedDay || !today) return false;
  return linkedDay < today;
};

/** Draft incomplete or overdue stub — still open for completion. */
export const isRecordStillOpen = record => Boolean(record && (record.draft || record.overdue));

/**
 * Link dateDue matches the checksheet's scheduled due (currentDateDue /
 * dateDue from due()). No physical record required.
 */
export const doesLinkDueMatchChecksheetDue = ({
  dateDueParam,
  checksheet,
  timezone = "US/Pacific"
}) => {
  if (!dateDueParam || !checksheet) return true;

  const linkDay = toCalendarDay(checksheet.targetDateDue ?? dateDueParam, timezone);
  const scheduledDue = checksheet.currentDateDue ?? checksheet.dateDue;
  const scheduledDay = toCalendarDay(scheduledDue, timezone);

  if (!linkDay || !scheduledDay) return false;
  return linkDay === scheduledDay;
};

/**
 * Upcoming/current link is outside its availability window (e.g. monthly due
 * later this month but window has not opened yet).
 */
export const isOutsideAvailabilityWindow = ({checksheet, recordDraft, timezone = "US/Pacific"}) => {
  if (!checksheet || recordDraft) return false;

  const openDay = toCalendarDay(checksheet.availableDate, timezone);
  const today = getTodayCalendarDay(timezone);
  return Boolean(openDay && today && today < openDay);
};

export const getExternalChecksheetStateMismatch = ({
  expectedFacilityStateId,
  checksheet,
  facilityStates = [],
  past = false,
  record = null
}) => {
  if (!expectedFacilityStateId || !checksheet) return null;

  const definedFacilityStateId = getDefinedFacilityStateId(checksheet);
  const definedStateActive = isDefinedFacilityStateActive(checksheet);
  const facilityName = checksheet.facility?.name?.toUpperCase() || "THIS FACILITY";

  const pastStillOpen = past && isRecordStillOpen(record);
  const recordKind = record?.draft ? "incomplete" : "overdue";

  if (expectedFacilityStateId === DEFAULT_FACILITY_STATE_ID) {
    if (!definedFacilityStateId || !definedStateActive) return null;

    const stateName = getFacilityStateNameById(definedFacilityStateId, facilityStates, checksheet);
    const onLabel = formatFacilityStateOnOff("ON", stateName);

    if (pastStillOpen) {
      return {
        type: "entered",
        message: `This ${recordKind} record was created while the facility was ${onLabel}.`
      };
    }

    return {
      type: "entered",
      message: `${facilityName} has been set to ${onLabel} since this reminder was sent. This checksheet link is no longer valid for completion. Please use the most updated link in your email inbox.`
    };
  }

  if (`${expectedFacilityStateId}` === `${definedFacilityStateId}` && definedStateActive) {
    return null;
  }

  const expectedName = getFacilityStateNameById(
    expectedFacilityStateId,
    facilityStates,
    checksheet
  );
  const offLabel = formatFacilityStateOnOff("OFF", expectedName);

  if (pastStillOpen) {
    return {
      type: "exited",
      message: `This ${recordKind} record was created while the facility was ${offLabel}.`
    };
  }

  return {
    type: "exited",
    message: `${facilityName} has been set to ${offLabel} since this reminder was sent. This checksheet link is no longer valid for completion. Please use the most updated link in your email inbox.`
  };
};

/**
 * External checksheet decision matrix.
 *
 * Upcoming / today:
 *   completed → completed
 *   wrong state → state_mismatch
 *   link date ≠ checksheet due → not_due
 *   window not open → not_due until window start
 *   else → form
 *
 * Past:
 *   completed → completed
 *   wasn't due (noRecordRequired) → not_due (beats state)
 *   was due + still open (draft|overdue) + wrong state → state_mismatch
 *   was due + still open + state ok → form
 *   else → not_due
 *
 * Locks only apply for form (handled by Checksheet.js).
 */
export const getExternalChecksheetOutcome = ({
  sessionExpired = false,
  dateDueParam = null,
  expectedFacilityStateId = null,
  checksheet = null,
  recordDraft = null,
  facilityStates = [],
  timezone = "US/Pacific"
}) => {
  if (sessionExpired) {
    return {type: EXTERNAL_OUTCOME.SESSION_EXPIRED};
  }

  if (!checksheet) return null;

  const draft = recordDraft || checksheet.recordDraft || null;
  const linkedDateDue = checksheet.targetDateDue ?? dateDueParam;
  const past = Boolean(dateDueParam) && isLinkedDayPast(linkedDateDue, timezone);
  const stillOpen = isRecordStillOpen(draft);

  const stateMismatch = getExternalChecksheetStateMismatch({
    expectedFacilityStateId,
    checksheet,
    facilityStates,
    past,
    record: draft
  });

  const completedForDate =
    Boolean(checksheet.completedForDate) ||
    (Boolean(dateDueParam) && Boolean(checksheet.completed) && !draft);

  if (completedForDate) {
    return {
      type: EXTERNAL_OUTCOME.COMPLETED,
      linkedDateDue
    };
  }

  // Past day with no row — wasn't due. Beats state.
  if (checksheet.noRecordRequiredForDate) {
    return {
      type: EXTERNAL_OUTCOME.NOT_DUE,
      linkedDateDue
    };
  }

  if (past) {
    if (stillOpen && stateMismatch) {
      return {
        type: EXTERNAL_OUTCOME.STATE_MISMATCH,
        message: stateMismatch.message,
        stateMismatch
      };
    }

    if (stillOpen) {
      return {type: EXTERNAL_OUTCOME.FORM, linkedDateDue};
    }

    return {
      type: EXTERNAL_OUTCOME.NOT_DUE,
      linkedDateDue
    };
  }

  // Upcoming / today (or no dateDue param).
  if (stateMismatch) {
    return {
      type: EXTERNAL_OUTCOME.STATE_MISMATCH,
      message: stateMismatch.message,
      stateMismatch
    };
  }

  if (dateDueParam && !doesLinkDueMatchChecksheetDue({dateDueParam, checksheet, timezone})) {
    return {
      type: EXTERNAL_OUTCOME.NOT_DUE,
      linkedDateDue
    };
  }

  if (isOutsideAvailabilityWindow({checksheet, recordDraft: draft, timezone})) {
    return {
      type: EXTERNAL_OUTCOME.NOT_DUE,
      linkedDateDue,
      availableUntil: checksheet.availableDate
    };
  }

  if (checksheet.completed && !draft) {
    return {
      type: EXTERNAL_OUTCOME.COMPLETED,
      linkedDateDue
    };
  }

  return {type: EXTERNAL_OUTCOME.FORM, linkedDateDue};
};
